(() => {
  'use strict';
 
  /* ------------------------------------------------------------------ */
  /* Configuration                                                       */
  /* ------------------------------------------------------------------ */
  const MEDIA_SELECTOR = '.product-gallery__media:not(.product-gallery__media--video)';
  const SLIDER_SELECTOR = '[data-product-single-media-slider], .flickity-enabled';
  const SLIDE_SELECTOR = '.product-gallery__media-slide';
 
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SCALE_OVERSHOOT_MAX = MAX_SCALE * 1.3; // hard ceiling while pinching
  const SCALE_OVERSHOOT_MIN = 0.7;             // hard floor while pinching
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_RADIUS = 30;                // px between taps
  const PAN_RESISTANCE = 0.3;                  // rubber-band factor at edges
  const INERTIA_FRICTION = 0.95;               // per-frame velocity decay
  const INERTIA_MIN_VELOCITY = 0.02;           // px/ms — below this, inertia stops
  const SNAP_DURATION = 280;                   // ms — snap-back / double-tap animation
  const ZOOM_EPSILON = 1.001;
 
  const STYLE_ID = 'pinch-zoom-gallery-styles';
  const CSS = `
    .product-gallery__media.pz-ready {
      overflow: hidden;
      touch-action: pan-y; /* keep vertical page scroll; block native pinch */
    }
    .product-gallery__media.pz-active {
      touch-action: none; /* gallery owns all gestures while zoomed */
    }
    .product-gallery__media .pz-target {
      transform-origin: 0 0;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    }
    .product-gallery__media.pz-active .pz-target {
      will-change: transform;
    }
    ${SLIDE_SELECTOR}.pz-slide-active {
      z-index: 2;
    }
  `;
 
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
 
  const getDistance = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  const getMidpoint = (a, b, rect) => ({
    x: (a.clientX + b.clientX) / 2 - rect.left,
    y: (a.clientY + b.clientY) / 2 - rect.top,
  });
 
  /* ------------------------------------------------------------------ */
  /* PinchZoom — one instance per gallery media element                  */
  /* ------------------------------------------------------------------ */
  class PinchZoom {
    /**
     * @param {HTMLElement} media — the .product-gallery__media element
     */
    constructor(media) {
      this.ok = false;
      this.media = media;
      this.target = media.querySelector('figure') ?? media.querySelector('img');
      if (!this.target) return;
 
      this.slide = media.closest(SLIDE_SELECTOR);
      this.sliderEl = media.closest(SLIDER_SELECTOR);
 
      // Transform state (transform-origin is 0 0)
      this.scale = 1;
      this.x = 0;
      this.y = 0;
 
      // Gesture state
      this.rect = null;
      this.pinching = false;
      this.panning = false;
      this.moved = false;
      this.locked = false;
      this.suppressClick = false;
      this.prevDraggable = null;
 
      this.lastDist = 0;
      this.lastMid = { x: 0, y: 0 };
      this.lastPoint = { x: 0, y: 0 };
      this.lastMoveTime = 0;
      this.velX = 0;
      this.velY = 0;
 
      this.lastTapTime = 0;
      this.lastTapPoint = { x: 0, y: 0 };
 
      this.renderPending = false;
      this.animFrame = null;     // snap / double-tap tween
      this.inertiaFrame = null;  // inertia loop
      this.resizeTimer = null;
 
      // Bound handlers (kept so destroy() can remove the exact references)
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onPointerGuard = this.onPointerGuard.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onGesturePrevent = this.onGesturePrevent.bind(this);
      this.onResize = this.onResize.bind(this);
 
      media.addEventListener('touchstart', this.onTouchStart, { passive: true });
      media.addEventListener('touchmove', this.onTouchMove, { passive: false });
      media.addEventListener('touchend', this.onTouchEnd, { passive: true });
      media.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      // Newer Flickity/Unidragger builds use Pointer Events — guard those too
      media.addEventListener('pointerdown', this.onPointerGuard, { passive: true });
      media.addEventListener('pointermove', this.onPointerGuard, { passive: true });
      // Block lightbox link / theme click handlers during & right after gestures
      media.addEventListener('click', this.onClickCapture, true);
      // iOS Safari proprietary gesture events — stop native page pinch on the gallery
      media.addEventListener('gesturestart', this.onGesturePrevent, { passive: false });
      media.addEventListener('gesturechange', this.onGesturePrevent, { passive: false });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
 
      media.classList.add('pz-ready');
      this.target.classList.add('pz-target');
      this.ok = true;
    }
 
    /* ----------------------------- helpers ----------------------------- */
 
    get isZoomed() {
      return this.scale > ZOOM_EPSILON;
    }
 
    getFlickity() {
      try {
        if (window.Flickity && this.sliderEl) return window.Flickity.data(this.sliderEl);
      } catch (err) {
        /* Flickity not ready — propagation guards still protect us */
      }
      return null;
    }
 
    /** Pan limits for a given scale (origin 0 0 → maxX/maxY are 0). */
    getBounds(scale = this.scale) {
      const w = this.rect?.width ?? this.media.offsetWidth;
      const h = this.rect?.height ?? this.media.offsetHeight;
      return {
        minX: Math.min(0, w - w * scale),
        maxX: 0,
        minY: Math.min(0, h - h * scale),
        maxY: 0,
      };
    }
 
    measure() {
      this.rect = this.media.getBoundingClientRect();
    }
 
    applyTransform() {
      this.target.style.transform =
        `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
    }
 
    scheduleRender() {
      if (this.renderPending) return;
      this.renderPending = true;
      requestAnimationFrame(() => {
        this.renderPending = false;
        this.applyTransform();
      });
    }
 
    cancelAnimations() {
      if (this.animFrame) cancelAnimationFrame(this.animFrame);
      if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame);
      this.animFrame = null;
      this.inertiaFrame = null;
    }
 
    /* --------------------- Flickity lock / unlock ---------------------- */
 
    lockSlider(event) {
      if (this.locked) return;
      this.locked = true;
      this.media.classList.add('pz-active');
      this.slide?.classList.add('pz-slide-active');
 
      const flkty = this.getFlickity();
      if (!flkty) return;
      try {
        this.prevDraggable = flkty.options.draggable;
        flkty.options.draggable = false;
        flkty.updateDraggable?.();
        // If Flickity already grabbed the first finger, cancel its drag cleanly
        if (flkty.isPointerDown && event?.touches?.[0]) {
          flkty.pointerCancel?.(event, event.touches[0]);
          flkty.select?.(flkty.selectedIndex, false, true);
        }
      } catch (err) {
        /* non-fatal: propagation guards keep the slider from moving */
      }
    }
 
    unlockSlider() {
      if (!this.locked) return;
      this.locked = false;
      this.media.classList.remove('pz-active');
      this.slide?.classList.remove('pz-slide-active');
 
      const flkty = this.getFlickity();
      if (!flkty) return;
      try {
        flkty.options.draggable = this.prevDraggable ?? true;
        flkty.updateDraggable?.();
      } catch (err) {
        /* ignore */
      }
    }
 
    /* --------------------------- touch flow ---------------------------- */
 
    onTouchStart(e) {
      this.cancelAnimations();
      this.moved = false;
 
      if (e.touches.length === 2) {
        this.measure();
        this.pinching = true;
        this.panning = false;
        this.lastDist = getDistance(e.touches[0], e.touches[1]);
        this.lastMid = getMidpoint(e.touches[0], e.touches[1], this.rect);
        this.lockSlider(e);
        e.stopPropagation();
        return;
      }
 
      if (e.touches.length === 1) {
        if (!this.rect) this.measure();
        const t = e.touches[0];
        this.lastPoint = { x: t.clientX, y: t.clientY };
        this.lastMoveTime = e.timeStamp;
        this.velX = 0;
        this.velY = 0;
        if (this.isZoomed) {
          this.panning = true;
          e.stopPropagation();
        }
      }
    }
 
    onTouchMove(e) {
      /* ---- pinch ---- */
      if (e.touches.length >= 2 && this.pinching) {
        e.preventDefault();
        e.stopPropagation();
 
        const dist = getDistance(e.touches[0], e.touches[1]);
        const mid = getMidpoint(e.touches[0], e.touches[1], this.rect);
        if (this.lastDist <= 0) {
          this.lastDist = dist;
          this.lastMid = mid;
          return;
        }
 
        let newScale = this.scale * (dist / this.lastDist);
        // Soft resistance past the limits, with hard stops
        if (newScale > MAX_SCALE) {
          newScale = Math.min(MAX_SCALE + (newScale - MAX_SCALE) * 0.25, SCALE_OVERSHOOT_MAX);
        } else if (newScale < MIN_SCALE) {
          newScale = Math.max(MIN_SCALE - (MIN_SCALE - newScale) * 0.25, SCALE_OVERSHOOT_MIN);
        }
 
        const applied = newScale / this.scale;
        // Zoom around the finger midpoint, then follow midpoint drift (two-finger pan)
        this.x = mid.x - (mid.x - this.x) * applied + (mid.x - this.lastMid.x);
        this.y = mid.y - (mid.y - this.y) * applied + (mid.y - this.lastMid.y);
        this.scale = newScale;
 
        this.lastDist = dist;
        this.lastMid = mid;
        this.moved = true;
        this.scheduleRender();
        return;
      }
 
      /* ---- one-finger pan while zoomed ---- */
      if (e.touches.length === 1 && this.isZoomed && !this.pinching) {
        e.preventDefault();
        e.stopPropagation();
 
        const t = e.touches[0];
        const dx = t.clientX - this.lastPoint.x;
        const dy = t.clientY - this.lastPoint.y;
        const dt = Math.max(1, e.timeStamp - this.lastMoveTime);
 
        const b = this.getBounds();
        this.x = this.resist(this.x, dx, b.minX, b.maxX);
        this.y = this.resist(this.y, dy, b.minY, b.maxY);
 
        // Smoothed velocity (px/ms) for inertia
        this.velX = 0.8 * (dx / dt) + 0.2 * this.velX;
        this.velY = 0.8 * (dy / dt) + 0.2 * this.velY;
 
        this.lastPoint = { x: t.clientX, y: t.clientY };
        this.lastMoveTime = e.timeStamp;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) this.moved = true;
        this.panning = true;
        this.scheduleRender();
      }
    }
 
    /** Move freely inside bounds; resist when pushing further past an edge. */
    resist(current, delta, min, max) {
      const next = current + delta;
      if ((next > max && delta > 0) || (next < min && delta < 0)) {
        return current + delta * PAN_RESISTANCE;
      }
      return next;
    }
 
    onTouchEnd(e) {
      // A finger lifted but one remains (end of pinch) → hand off to panning
      if (e.touches.length === 1) {
        if (this.pinching) {
          this.pinching = false;
          const t = e.touches[0];
          this.lastPoint = { x: t.clientX, y: t.clientY };
          this.lastMoveTime = e.timeStamp;
          this.velX = 0;
          this.velY = 0;
          this.panning = this.isZoomed;
        }
        return;
      }
      if (e.touches.length > 0) return;
 
      /* ---- all fingers up ---- */
      const wasPinching = this.pinching;
      const wasPanning = this.panning;
      this.pinching = false;
      this.panning = false;
 
      if (this.moved) this.suppressClick = true;
 
      // Double-tap (only counts when the finger didn't move)
      if (!this.moved && !wasPinching) {
        const t = e.changedTouches[0];
        const now = e.timeStamp;
        const dx = t.clientX - this.lastTapPoint.x;
        const dy = t.clientY - this.lastTapPoint.y;
        const isDoubleTap =
          now - this.lastTapTime < DOUBLE_TAP_MS && Math.hypot(dx, dy) < DOUBLE_TAP_RADIUS;
 
        if (isDoubleTap) {
          this.lastTapTime = 0;
          this.suppressClick = true;
          this.onDoubleTap(t);
          return;
        }
        this.lastTapTime = now;
        this.lastTapPoint = { x: t.clientX, y: t.clientY };
      }
 
      // Out-of-bounds scale or position → animated snap-back
      if (this.needsSettle()) {
        this.settle();
        return;
      }
 
      // Clean pan inside bounds → inertia glide
      if (wasPanning && this.isZoomed) {
        this.startInertia();
        return;
      }
 
      if (!this.isZoomed) this.resetState();
    }
 
    /* ------------------------ pointer/click guards --------------------- */
 
    onPointerGuard(e) {
      // Keep pointer-event-based Flickity builds from seeing gestures we own
      if (this.isZoomed || this.pinching) e.stopPropagation();
    }
 
    onClickCapture(e) {
      if (this.suppressClick || this.isZoomed) {
        e.preventDefault();
        e.stopPropagation();
        this.suppressClick = false;
      }
    }
 
    onGesturePrevent(e) {
      e.preventDefault();
    }
 
    onResize() {
      // Geometry changed under a zoomed image → reset for correctness
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.isZoomed || this.locked) {
          this.cancelAnimations();
          this.resetState();
        }
      }, 150);
    }
 
    /* --------------------------- animations ---------------------------- */
 
    needsSettle() {
      const s = clamp(this.scale, MIN_SCALE, MAX_SCALE);
      if (s !== this.scale) return true;
      const b = this.getBounds(s);
      return (
        this.x > b.maxX || this.x < b.minX ||
        this.y > b.maxY || this.y < b.minY
      );
    }
 
    /** Snap scale/position back into legal bounds with an eased tween. */
    settle() {
      const targetScale = clamp(this.scale, MIN_SCALE, MAX_SCALE);
      const cx = (this.rect?.width ?? this.media.offsetWidth) / 2;
      const cy = (this.rect?.height ?? this.media.offsetHeight) / 2;
 
      // Re-scale around the container centre, then clamp into bounds
      const k = targetScale / this.scale;
      let tx = cx - (cx - this.x) * k;
      let ty = cy - (cy - this.y) * k;
      const b = this.getBounds(targetScale);
      tx = clamp(tx, b.minX, b.maxX);
      ty = clamp(ty, b.minY, b.maxY);
 
      this.animateTo(targetScale, tx, ty);
    }
 
    onDoubleTap(touch) {
      if (!this.rect) this.measure();
      if (this.isZoomed) {
        this.animateTo(1, 0, 0);
        return;
      }
      const fx = touch.clientX - this.rect.left;
      const fy = touch.clientY - this.rect.top;
      const s = DOUBLE_TAP_SCALE;
      const b = this.getBounds(s);
      const tx = clamp(fx - (fx - this.x) * (s / this.scale), b.minX, b.maxX);
      const ty = clamp(fy - (fy - this.y) * (s / this.scale), b.minY, b.maxY);
      this.lockSlider();
      this.animateTo(s, tx, ty);
    }
 
    animateTo(scale, x, y, duration = SNAP_DURATION) {
      this.cancelAnimations();
      const from = { scale: this.scale, x: this.x, y: this.y };
      const start = performance.now();
 
      const step = (now) => {
        const p = easeOutCubic(clamp((now - start) / duration, 0, 1));
        this.scale = from.scale + (scale - from.scale) * p;
        this.x = from.x + (x - from.x) * p;
        this.y = from.y + (y - from.y) * p;
        this.applyTransform();
 
        if (p < 1) {
          this.animFrame = requestAnimationFrame(step);
        } else {
          this.animFrame = null;
          if (!this.isZoomed) this.resetState();
        }
      };
      this.animFrame = requestAnimationFrame(step);
    }
 
    startInertia() {
      this.cancelAnimations();
      let vx = this.velX;
      let vy = this.velY;
      if (Math.abs(vx) < INERTIA_MIN_VELOCITY && Math.abs(vy) < INERTIA_MIN_VELOCITY) return;
 
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min(48, now - last);
        last = now;
 
        this.x += vx * dt;
        this.y += vy * dt;
        vx *= INERTIA_FRICTION;
        vy *= INERTIA_FRICTION;
 
        // Hard-stop each axis at its boundary
        const b = this.getBounds();
        if (this.x > b.maxX) { this.x = b.maxX; vx = 0; }
        if (this.x < b.minX) { this.x = b.minX; vx = 0; }
        if (this.y > b.maxY) { this.y = b.maxY; vy = 0; }
        if (this.y < b.minY) { this.y = b.minY; vy = 0; }
 
        this.applyTransform();
 
        if (Math.abs(vx) > INERTIA_MIN_VELOCITY || Math.abs(vy) > INERTIA_MIN_VELOCITY) {
          this.inertiaFrame = requestAnimationFrame(step);
        } else {
          this.inertiaFrame = null;
        }
      };
      this.inertiaFrame = requestAnimationFrame(step);
    }
 
    /** Hard reset to scale 1 and restore Flickity. */
    resetState() {
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.target.style.transform = '';
      this.unlockSlider();
    }
 
    /* ----------------------------- teardown ---------------------------- */
 
    destroy() {
      this.cancelAnimations();
      clearTimeout(this.resizeTimer);
      this.resetState();
 
      const m = this.media;
      m.removeEventListener('touchstart', this.onTouchStart);
      m.removeEventListener('touchmove', this.onTouchMove);
      m.removeEventListener('touchend', this.onTouchEnd);
      m.removeEventListener('touchcancel', this.onTouchEnd);
      m.removeEventListener('pointerdown', this.onPointerGuard);
      m.removeEventListener('pointermove', this.onPointerGuard);
      m.removeEventListener('click', this.onClickCapture, true);
      m.removeEventListener('gesturestart', this.onGesturePrevent);
      m.removeEventListener('gesturechange', this.onGesturePrevent);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
 
      m.classList.remove('pz-ready', 'pz-active');
      this.slide?.classList.remove('pz-slide-active');
      this.target.classList.remove('pz-target');
    }
  }
 
  /* ------------------------------------------------------------------ */
  /* Bootstrapping                                                       */
  /* ------------------------------------------------------------------ */
  const registry = new Set();
 
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
 
  function initAll(root = document) {
    injectStyles();
    root.querySelectorAll(MEDIA_SELECTOR).forEach((media) => {
      if (media.classList.contains('pz-ready')) return;
      const instance = new PinchZoom(media);
      if (instance.ok) registry.add(instance);
    });
  }
 
  function destroyWithin(root) {
    registry.forEach((instance) => {
      if (root.contains(instance.media)) {
        instance.destroy();
        registry.delete(instance);
      }
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
 
  // Shopify theme editor & dynamically (re)rendered sections
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyWithin(e.target));
})();